import get from 'lodash.get';
import difference from 'lodash.difference';
import { Loading } from 'element-ui';
import { getWeekStartAndEndDate } from '@/helpers';
import {
  findWeekOrders,
  findWeekOrdersByUser,
  placeOrder,
  deleteOrderByMenuEvent,
  deleteOrdersByDay,
  deleteWeekOrders,
  deleteWeekOrdersByEvent,
  deleteOrderById,
  checkUserOrder,
  createOrders,
} from '@/services/order';

// eslint-disable-next-line no-unused-vars
const initializeWeekOrders = async ({ commit }, { office, weekNumber }) => {
  commit('SET_LOADING', true);
  const weekDates = getWeekStartAndEndDate(weekNumber);
  const weekOrders = await findWeekOrders({
    office,
    startDate: weekDates.startDate.format('YYYY-MM-DD'),
    endDate: weekDates.endDate.format('YYYY-MM-DD'),
  });
  commit('SET_LOADING', false);
  return get(weekOrders, 'data.findWeekOrders');
};

const initializeWeekOrdersByUser = async ({ commit, rootGetters }, { weekNumber }) => {
  const user = await rootGetters['user/getUser'];
  const localUserOrders = await rootGetters['orders/getWeekOrdersByUser'];
  const weekDates = getWeekStartAndEndDate(weekNumber);
  const weekOrdersByUser = await findWeekOrdersByUser({
    office: user.office,
    startDate: weekDates.startDate.format('YYYY-MM-DD'),
    endDate: weekDates.endDate.format('YYYY-MM-DD'),
  });
  commit(
    'SET_USER_ORDERS_IDS_BEFORE_SYNC',
    localUserOrders.map((meal) => meal.id)
  );
  return get(weekOrdersByUser, 'data.findWeekOrdersByUser');
};

const validateUserOrdersSync = async ({ commit, dispatch, rootGetters, state }, weekNumber) => {
  const fullScreenLoading = Loading.service({ fullscreen: true });
  await dispatch('initializeWeekOrdersByUser', { weekNumber });
  const localUserOrdersIds = await rootGetters['orders/getUserOrdersIdsBeforeSync'];
  const dbUserOrders = await rootGetters['orders/getWeekOrdersByUser'];
  const { requestByUserWeekSuccess } = state;
  const dbUserOrdersIds = dbUserOrders.map((meal) => meal.id);

  let ordersWereUpdated = false;
  if (requestByUserWeekSuccess && localUserOrdersIds.length === dbUserOrdersIds.length) {
    ordersWereUpdated = difference(dbUserOrdersIds, localUserOrdersIds).length === 0;
  }

  commit('SET_ORDERS_UPDATED', ordersWereUpdated);
  fullScreenLoading.close();
  const modalType = ordersWereUpdated ? 'success' : 'failure';

  commit(
    'global/SET_MESSAGE_MODAL',
    {
      title: `weeklyMenuPage.modal.${modalType}Title`,
      message: `weeklyMenuPage.modal.${modalType}Message`,
    },
    { root: true }
  );
};

const insertOrder = async ({ commit }, order) => {
  const placeOrderResponse = await placeOrder(order);
  if (!placeOrderResponse.errors) {
    commit('INSERT_ORDER', get(placeOrderResponse, 'data.placeOrder'));
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.insertOrderSuccess' },
      { root: true }
    );
  } else {
    commit(
      'global/SET_MESSAGE_ERROR',
      { message: 'calendar.messages.insertOrderFail' },
      { root: true }
    );
  }
  return order;
};

const removeOrderById = async ({ commit }, orderId) => {
  const orderDestroyedResult = await deleteOrderById(orderId);
  if (!orderDestroyedResult.errors) {
    commit('REMOVE_ORDER', orderId);
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.removeMealSuccess' },
      { root: true }
    );
  } else {
    commit(
      'global/SET_MESSAGE_ERROR',
      { message: 'calendar.messages.removeMealFail' },
      { root: true }
    );
  }
  return orderId;
};

const removeOrderByMenuEvent = async ({ commit }, order) => {
  const removeOrderResponse = await deleteOrderByMenuEvent(order);
  if (!removeOrderResponse.errors) {
    commit('REMOVE_ORDERS_BY_MENU_EVENT', order);
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.removeMealSuccess' },
      { root: true }
    );
  } else {
    commit(
      'global/SET_MESSAGE_ERROR',
      { message: 'calendar.messages.removeMealFail' },
      { root: true }
    );
  }
  return order;
};

const removeOrdersByDay = async ({ commit }, { date, office, startDate, endDate }) => {
  const removeOrdersResponse = await deleteOrdersByDay({ date, office, startDate, endDate });
  if (!removeOrdersResponse.errors) {
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.removeMealDaySuccess' },
      { root: true }
    );
    return get(removeOrdersResponse, 'data.deleteOrdersByDay');
  }
  commit('UPDATE_LOCAL_ORDERS_DATE', date);
  commit(
    'global/SET_MESSAGE_ERROR',
    { message: 'calendar.messages.removeMealDayFail' },
    { root: true }
  );
  return [];
};

const removeWeekOrdersByEvent = async ({ commit }, weekEvent) => {
  const removeOrdersByEventResponse = await deleteWeekOrdersByEvent(weekEvent);
  if (!removeOrdersByEventResponse.errors) {
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.removeMealEventSuccess' },
      { root: true }
    );
    return get(removeOrdersByEventResponse, 'data.deleteWeekOrdersByEvent');
  }
  commit('UPDATE_LOCAL_ORDERS_EVENT', weekEvent.mealEvent);
  commit(
    'global/SET_MESSAGE_ERROR',
    { message: 'calendar.messages.removeMealEventFail' },
    { root: true }
  );
  return [];
};

const removeWeekOrders = async ({ commit }, weekEvent) => {
  const deleteWeekOrdersResponse = await deleteWeekOrders(weekEvent);
  if (!deleteWeekOrdersResponse.errors) {
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.removeMealWeekSuccess' },
      { root: true }
    );
  } else {
    commit(
      'global/SET_MESSAGE_ERROR',
      { message: 'calendar.messages.removeMealWeekFail' },
      { root: true }
    );
  }
  return [];
};

const checkOutUserOrder = async ({ commit }, { orderId, served }) => {
  const checkUserOrderResponse = await checkUserOrder({ orderId, served });
  if (!checkUserOrderResponse.errors) {
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.updateOrderSuccess' },
      { root: true }
    );
  } else {
    commit(
      'global/SET_MESSAGE_ERROR',
      { message: 'calendar.messages.updateOrderFail' },
      { root: true }
    );
  }
  return [];
};

const createBulkOrders = async ({ commit }, order) => {
  const createOrdersResponse = await createOrders(order);
  if (!createOrdersResponse.errors) {
    commit('SET_BULK_ORDERS_UPDATED', true);
    commit(
      'global/SET_MESSAGE_SUCCESS',
      { message: 'calendar.messages.insertOrderSuccess' },
      { root: true }
    );
  } else {
    commit('SET_BULK_ORDERS_UPDATED', false);
    commit(
      'global/SET_MESSAGE_ERROR',
      { message: 'calendar.messages.insertOrderFail' },
      { root: true }
    );
  }
  return order;
};

export default {
  initializeWeekOrders,
  initializeWeekOrdersByUser,
  insertOrder,
  removeOrderById,
  removeOrderByMenuEvent,
  removeOrdersByDay,
  removeWeekOrders,
  removeWeekOrdersByEvent,
  validateUserOrdersSync,
  checkOutUserOrder,
  createBulkOrders,
};



// WEBPACK FOOTER //
// ./store/modules/orders/actions.js